/**
 * The small badge next to a dimension whose score actually changed once the
 * interview completed. Reuses CaptionInfo (fe#59) rather than a new
 * disclosure pattern -- "never throw away data" applied to score history:
 * the pre-interview score and its justification stay visible on demand
 * instead of being overwritten and lost.
 *
 * Passes `accent` so the icon reads as the same semantic as this page's
 * existing "AI"-generated badges -- a dimension the interview changed is,
 * definitionally, AI-assessed content, per the design spec's own reasoning.
 * `in_progress`'s badge deliberately does NOT use this accent (see
 * InterviewStatusBadge): "in progress" isn't a good/bad outcome the accent
 * should mark, it's an in-flight state, styled after statusPalette.ts's
 * `active` tone instead.
 */

import { memo } from 'react';
import CaptionInfo from '../../../../../components/common/caption-info';

export interface DimensionScoreHistoryProps {
  previousScore: number;
  previousJustification: string;
  /** Out of how many, e.g. 5. Matches DimensionRow's own SCALE_MAX. */
  scaleMax?: number;
}

export const DimensionScoreHistory = memo(
  ({
    previousScore,
    previousJustification,
    scaleMax = 5,
  }: DimensionScoreHistoryProps) => (
    <CaptionInfo
      accent
      label="Score before the interview"
      text={`Pre-interview: ${previousScore}/${scaleMax} — ${previousJustification}`}
    />
  ),
);

DimensionScoreHistory.displayName = 'DimensionScoreHistory';

export default DimensionScoreHistory;
